/**
 * Utility functions for handling navigation state in Next.js
 * Since Next.js doesn't support location.state like React Router,
 * we serialize state to query parameters
 */

/**
 * Serialize a state object into a URL query string
 * @param state - Object to serialize
 * @returns Query string (without leading ?)
 */
export function serializeState(state: Record<string, any>): string {
  const params = new URLSearchParams()

  Object.entries(state).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.set(key, String(value))
    }
  })

  return params.toString()
}

/**
 * Deserialize query parameters into a state object
 * @param searchParams - URLSearchParams instance
 * @returns Deserialized state object
 */
export function deserializeState(searchParams: URLSearchParams): Record<string, string> {
  const state: Record<string, string> = {}

  searchParams.forEach((value, key) => {
    state[key] = value
  })

  return state
}

/**
 * Navigate to a URL with state serialized as query parameters
 * @param url - Base URL to navigate to
 * @param state - State object to serialize
 * @param router - Next.js router instance
 */
export function navigateWithState(
  url: string,
  state: Record<string, any>,
  router: { push: (url: string) => void },
): void {
  const queryString = serializeState(state)
  const fullUrl = queryString ? `${url}?${queryString}` : url
  router.push(fullUrl)
}

/**
 * Get a state value from search params, with optional type conversion
 * @param searchParams - URLSearchParams instance
 * @param key - Key to retrieve
 * @param defaultValue - Default value if key not found
 * @returns The value or default
 */
export function getStateValue<T = string>(
  searchParams: URLSearchParams,
  key: string,
  defaultValue?: T,
): T | string | undefined {
  const value = searchParams.get(key)

  if (value === null) {
    return defaultValue
  }

  // Try to parse booleans
  if (value === 'true') return true as unknown as T
  if (value === 'false') return false as unknown as T

  // Skip number parsing for hex addresses (Ethereum addresses start with 0x and are 42 chars)
  // Also skip if value contains letters that aren't part of a valid number (e.g., 'e' in scientific notation)
  const isHexAddress = value.startsWith('0x') && value.length === 42
  const containsNonNumericChars =
    /[a-fA-F]/.test(value) && !/^0x[0-9a-fA-F]+$/.test(value)

  // Try to parse numbers (but skip hex addresses and strings with letters)
  if (
    !isHexAddress &&
    !containsNonNumericChars &&
    !isNaN(Number(value)) &&
    value !== ''
  ) {
    // Additional check: if it's a valid number string (not scientific notation from hex conversion)
    const numValue = Number(value)
    // If the string representation doesn't match the original, it was likely converted (e.g., from hex)
    if (String(numValue) === value || (!value.includes('e') && !value.includes('E'))) {
      return numValue as unknown as T
    }
  }

  return value as unknown as T
}
